"use client";
import { VideoPlaylistType } from "@/app/types/playlist";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import { ListVideo } from "lucide-react";
import Link from "next/link";

type Props = {
    playlists: VideoPlaylistType[];
    currentVideoId: number;
};

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function VideoPlaylistPanel({ playlists, currentVideoId }: Props) {
    return (
        <Stack gap={2} sx={{ p: 2, pt: { md: 0 } }}>
            {playlists.map((playlist) => (
                <Paper key={playlist.id} variant="outlined" sx={{ overflow: "hidden" }}>
                    <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <ListVideo size={16} />
                            <Typography fontWeight={700} fontSize={14}>
                                {playlist.title}
                            </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {playlist.videos.length} ویدیو
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ maxHeight: { md: 460 }, overflowY: { md: "auto" } }}>
                        {playlist.videos.map((video, index) => {
                            const isActive = video.id === currentVideoId;
                            return (
                                <Box
                                    key={video.id}
                                    component={Link}
                                    href={`/fa/videos/${video.slug}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 1,
                                        pr: 2,
                                        py: 1,
                                        textDecoration: "none",
                                        color: "inherit",
                                        bgcolor: isActive ? "action.selected" : "transparent",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        "&:last-child": { borderBottom: "none" },
                                        "&:hover": {
                                            bgcolor: isActive ? "action.selected" : "action.hover",
                                        },
                                    }}
                                >
                                    <MediaV2
                                        media_id={video.poster?.id ?? video.media.id}
                                        alt={video.title}
                                        width={160}
                                        height={90}
                                        options={video.poster ? undefined : { requestPoster: true }}
                                        imageProps={{
                                            style: {
                                                height: '100%',
                                                width: 'auto'
                                            }
                                        }}
                                        boxProps={{ sx: { width: 80, height: 45, flexShrink: 0, borderRadius: 0.5, bgcolor: '#000' } }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color={isActive ? "primary.main" : "text.disabled"}
                                        sx={{ minWidth: 20, textAlign: "center", flexShrink: 0 }}
                                    >
                                        {index + 1}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: 1,
                                            fontWeight: isActive ? 700 : 400,
                                            color: isActive ? "primary.main" : "inherit",
                                        }}
                                    >
                                        {video.title}
                                    </Typography>
                                    {video.media?.video?.duration != null && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ flexShrink: 0, direction: "ltr" }}
                                        >
                                            {formatDuration(video.media.video.duration)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            ))}
        </Stack>
    );
}
